import { useSsoAccessToken } from "@/composables/user";
import { createAccountSetupLink, router } from "@/router";
import { trackNewRegistrationEvent } from "@/services/analytics/helpers";
import { ApiClient } from "@/services/apiClient";
import { Event, EventBus } from "@/services/eventBus";
import { afterLogin } from "@/services/user/auth/login/afterLoginService";
import { Authentication } from "@/services/user/auth/login/authenticationService";
import { setupUserInApp } from "@/services/user/auth/login/helpers";
import { LoginProvider, ThirdPartyLoginStatus } from "@/services/user/auth/login/sso/types";
import { AccountType, type User } from "@/services/user/types";
import { UserNotEstablishedException } from "@/services/user/userExceptions";

type ApiResponse_ThirdPartyLoginAuthentication_ok = {
  status: ThirdPartyLoginStatus.ok;
  user: User;
  isFirstLogin: boolean;
};

type ApiResponse_ThirdPartyLoginAuthentication_error = {
  status: ThirdPartyLoginStatus.accountInactive | ThirdPartyLoginStatus.differentAccountType | ThirdPartyLoginStatus.wrongData;
  message: string;
};

type ApiResponse_ThirdPartyLoginAuthentication = ApiResponse_ThirdPartyLoginAuthentication_ok | ApiResponse_ThirdPartyLoginAuthentication_error;

export async function ssoLogin(token: string, provider: LoginProvider): Promise<void> {
  return thirdPartyLogin(token, false, provider).then((response: ApiResponse_ThirdPartyLoginAuthentication) => {
    if (response.status === ThirdPartyLoginStatus.ok) {
      if (response.isFirstLogin) {
        let accountType;
        if (provider === LoginProvider.facebook) {
          accountType = AccountType.facebook;
        } else if (provider === LoginProvider.google) {
          accountType = AccountType.google;
        } else {
          throw new Error("DEV: Should not happen.");
        }
        const userId = response.user.id;
        trackNewRegistrationEvent(userId, accountType);
      }

      Authentication.resolveManually();
      return setupUserInApp(response.user)
        .then(async () => {
          useSsoAccessToken().value = token;
          await afterLogin();
        })
        .catch((e: unknown) => {
          if (e instanceof UserNotEstablishedException) {
            const redirect = createAccountSetupLink();
            router.push(redirect);
          } else {
            throw e;
          }
        });
    } else {
      EventBus.emit({ event: Event.ToastError, data: response.message });
    }
  });
}

/** @deprecated */
export async function thirdPartyLogin(token: string, registerNewsletter: boolean, provider: LoginProvider): Promise<ApiResponse_ThirdPartyLoginAuthentication> {
  const url = "user/third-party-login";
  const data = {
    token,
    provider,
    newsletter: registerNewsletter ? 1 : 0,
  };

  return ApiClient.postPublic<ApiResponse_ThirdPartyLoginAuthentication>(url, {}, data).then((response) => response.data);
}
